import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { RouteService } from './route.service';
import { AuthUtil } from '../util/auth-util';
import { ApiUrlUtil } from '../util/api-url-util';
import { LayoutService, ToastService } from 'codex-lib';
import { TranslateService } from '@ngx-translate/core';
import * as R from 'ramda';
import { CookieWrapperService } from './cookie-wrapper.service';
import { AccessTokenService } from '../modules/signature/services/access-token.service';
import { AuthUtilService } from './auth-util.service';
import { SKIP_ERROR_TOAST } from './http-error-options';

@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {

  constructor(
    private authUtilService: AuthUtilService,
    private routeService: RouteService,
    private authService: AuthService,
    private toastService: ToastService,
    private layoutService: LayoutService,
    private translate: TranslateService,
    private cookieWrapperService: CookieWrapperService,
    private accessTokenService: AccessTokenService,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipErrorToast = request.context.get(SKIP_ERROR_TOAST);
    let curHeaders = request.headers;
    curHeaders = curHeaders.append('Access-Control-Allow-Origin', '*');
    curHeaders = curHeaders.append('Access-Control-Allow-Headers',
      'Access-Control-Allow-Headers, Origin,Accept, X-Requested-With,' +
      'Content-Type, Access-Control-Request-Method, Access-Control-Request-Headers');
    curHeaders = curHeaders.append('Access-Control-Allow-Methods', 'GET, POST, OPTIONS, PUT, PATCH, DELETE');
    request = request.clone({
      headers: curHeaders
    });

    request = this.addAuthenticationToken(request);

    const apiReq = request.url.startsWith('.') || request.url.startsWith('https') ?
      request : request.clone({ url: this.fillRequestUrl(request.url) });

    return next.handle(apiReq)
      .pipe(
        tap(
          (event) => { },
          (errorResponse) => {
            const errorsRedirectToAccessToken = [
              'error.authentication.AccessTokenCannotBeNull',
              'error.authentication.envelopeSignatoryNotFound',
              'error.authentication.envelopeRequireAccessToken',
              'error.authentication.invalidOrExpiredAccessToken',
              'error.authentication.EnvelopeAlreadySigned',
              'error.authentication.EnvelopeAlreadySignedConcluded',
              'error.authentication.EnvelopeSignatoryDeleted',
              'error.authentication.EnvelopeAlreadyExcluded',
              'error.authentication.EnvelopeAlreadyApproved',
              'error.authentication.EnvelopeAlreadyReproved',
              'error.authentication.EnvelopeAlreadyExpired',
              'error.authentication.EnvelopeAuthorizerAlreadyReproved',
              'error.authentication.EnvelopeOrderWasChanged',
              'error.authentication.EnvelopeSignatureRejected',
              'error.authentication.EnvelopResponsibleAlreadyFilled',
              'error.authentication.EnvelopeSignatoryAcessDenied',
              'Unauthorized',
            ];

            if (errorResponse.status === 400) {
              this.authService.refreshToken();
            } else if (errorResponse.status === 401) {
              if (
                errorResponse.error &&
                errorsRedirectToAccessToken.indexOf(errorResponse.error.errorId) >
                -1
              ) {
                this.authService.setErrorId(errorResponse.error.errorId);
                if (
                  errorResponse.error.errorId ===
                  'error.authentication.EnvelopeOrderWasChanged' ||
                  errorResponse.error.errorId ===
                  'error.authentication.EnvelopeSignatoryDeleted' ||
                  errorResponse.error.errorId ===
                  'error.authentication.EnvelopeSignatoryAcessDenied'
                ) {
                  this.routeService.redirectToDeniedPage();
                } else if (
                  errorResponse.error.errorId !==
                  'error.authentication.envelopeSignatoryNotFound' &&
                  errorResponse.error.errorId !==
                  'error.authentication.EnvelopeAlreadySigned' &&
                  errorResponse.error.errorId !==
                  'error.authentication.EnvelopeAlreadySignedConcluded' &&
                  errorResponse.error.errorId !==
                  'error.authentication.EnvelopeAlreadyExcluded' &&
                  errorResponse.error.errorId !==
                  'error.authentication.EnvelopeAlreadyApproved' &&
                  errorResponse.error.errorId !==
                  'error.authentication.EnvelopeAlreadyReproved' &&
                  errorResponse.error.errorId !==
                  'error.authentication.EnvelopeAlreadyExpired' &&
                  errorResponse.error.errorId !==
                  'error.authentication.EnvelopeAuthorizerAlreadyReproved' &&
                  errorResponse.error.errorId !==
                  'error.authentication.EnvelopeSignatureRejected' &&
                  errorResponse.error.errorId !==
                  'error.authentication.EnvelopResponsibleAlreadyFilled'
                ) {
                  this.routeService.redirectToAccessToken();
                } else if(
                  errorResponse.error.errorId ===
                  'error.authentication.EnvelopResponsibleAlreadyFilled'
                ) {
                  this.routeService.redirectToPageResponsibleAlreadyFilled();
                } else if (
                  errorResponse.error.errorId ===
                  'error.authentication.EnvelopeAlreadySigned'
                ) {
                  this.routeService.redirectToPageAlreadySigned();
                } else if (
                  errorResponse.error.errorId ===
                  'error.authentication.EnvelopeAlreadySignedConcluded'
                ) {
                  this.routeService.redirectToPageAlreadySignedConcluded();
                } else if(
                  errorResponse.error.errorId ===
                  'error.authentication.EnvelopeSignatureRejected'
                ) {
                  this.routeService.redirectToPageDocumentRejected();
                } else {
                  this.routeService.redirectToErrorPage();
                }
              } else {
                this.routeService.logoutAndGoToSuiteRoot();
              }
            }
            if (!skipErrorToast && errorResponse.error && errorResponse.error.Title) {
              if (errorResponse.error.Errors) {
                for (const error of errorResponse.error.Errors) {
                  if (error.split('.')[0] === 'businessError') {
                    this.toastService.createNewToast({
                      text: this.translate.instant(error),
                      autoHide: true,
                    });
                  }
                }
              } else {
                this.toastService.createNewToast({
                  text: this.translate.instant(errorResponse.error.Title),
                  autoHide: true,
                });
              }
              this.layoutService.toggleLoading(false);
            } else if (
              errorResponse.status === 502 ||
              (errorResponse.status === 500 &&
                R.pathOr('', ['url'], errorResponse).includes('authuser/login'))
            ) {
              this.routeService.logoutAndGoToSuiteRoot();
            }
          }
        )
      );
  }

  private fillRequestUrl(requestUrl: string): string {
    if (requestUrl.indexOf('rest/suite-auth') >= 0) {
      return requestUrl;
    }

    const baseUrl = ApiUrlUtil.getFormattedUrl();
    requestUrl = requestUrl.replace(`${baseUrl}/`, '');
    const url = `${baseUrl}/${requestUrl}`;
    return url;
  }

  private addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
    const accessKey = this.accessTokenService.getAccessKey();

    if (accessKey) {
      request = request.clone({
        headers: request.headers.set(AuthUtil.AUTH_HEADER_ACCESSKEY, accessKey)
      });
    }

    const jwtToken = this.cookieWrapperService.getJwtToken();
    if (jwtToken) {
      return request.clone({
        headers: request.headers.set(AuthUtil.AUTH_HEADER, jwtToken)
          .set(AuthUtil.AUTH_USER_SIGN, this.authUtilService.getUserSingLocalStorage())
      });

    } else {
      return request = request.clone({
        setHeaders: {
          'Content-Type': 'application/json'
        }
      });
    }
  }
}
